import React from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
  ChartData as ChartJSData,
} from 'chart.js'
import { Line, Bar, Doughnut, Pie } from 'react-chartjs-2'
import 'chart.js/auto'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
)

interface DataPoint {
  x: string | number
  y: number
  label?: string
}

interface SimpleNextJSChartProps {
  data: DataPoint[]
  type: 'line' | 'bar' | 'area' | 'doughnut' | 'pie'
  title?: string
  color?: string
  height?: number
  width?: number
  showGrid?: boolean
  showLegend?: boolean
  showTooltip?: boolean
  animationDuration?: number
  gradient?: boolean
  curved?: boolean
  onDataPointClick?: (point: DataPoint, index: number) => void
}

/** Resolve CSS variables (e.g. var(--primary)) to a value Chart.js can use in canvas */
function resolveChartColor(cssColor: string): string {
  if (typeof document === 'undefined') return cssColor
  const trimmed = cssColor.trim()
  if (trimmed.startsWith('var(')) {
    const match = trimmed.match(/var\((--[a-zA-Z0-9-]+)\)/)
    if (match) {
      const value = getComputedStyle(document.documentElement).getPropertyValue(match[1]).trim()
      if (value) return `hsl(${value})`
    }
  }
  return cssColor
}

function getThemeColor(varName: string): string {
  if (typeof document === 'undefined') return 'rgba(255,255,255,0.5)'
  const value = getComputedStyle(document.documentElement).getPropertyValue(varName).trim()
  return value ? `hsl(${value})` : 'rgba(255,255,255,0.5)'
}

/** Return the same color with alpha (for gradients). Handles hsl() and hex. */
function withAlpha(color: string, alpha: number): string {
  const hslMatch = color.match(/hsl\(([^)]+)\)/)
  if (hslMatch) {
    const rest = hslMatch[1].trim()
    const parts = rest.split(/[\s,]+/)
    if (parts.length >= 3) {
      const [h, s, l] = parts
      return `hsla(${h}, ${s}, ${l}, ${alpha})`
    }
    return `hsla(${rest}, ${alpha})`
  }
  if (color.startsWith('#') && color.length === 7) {
    const hex = color.slice(1)
    const r = parseInt(hex.slice(0, 2), 16)
    const g = parseInt(hex.slice(2, 4), 16)
    const b = parseInt(hex.slice(4, 6), 16)
    return `rgba(${r},${g},${b},${alpha})`
  }
  return color
}

const SimpleNextJSChart: React.FC<SimpleNextJSChartProps> = ({
  data,
  type,
  title,
  color = 'var(--primary)',
  height = 300,
  width,
  showGrid = true,
  showLegend = false,
  showTooltip = true,
  animationDuration = 750,
  gradient = true,
  curved = true,
  onDataPointClick
}) => {
  const resolvedColor = resolveChartColor(color)
  const foreground = getThemeColor('--foreground')
  const mutedForeground = getThemeColor('--muted-foreground')
  const border = getThemeColor('--border')
  const card = getThemeColor('--card')
  const background = getThemeColor('--background')
  // Prepare data based on chart type
  let preparedData: ChartJSData<any>
  let preparedOptions: ChartOptions<any>

  if (type === 'doughnut' || type === 'pie') {
    preparedData = {
      labels: data.map((point, index) => point.label || `Item ${index + 1}`),
      datasets: [{
        data: data.map(point => point.y),
        backgroundColor: [
          resolvedColor,
          `${resolvedColor}CC`,
          `${resolvedColor}99`,
          `${resolvedColor}66`,
          `${resolvedColor}33`,
          '#FF6B6B',
          '#4ECDC4',
          '#45B7D1',
          '#96CEB4',
          '#FFEAA7'
        ],
        borderColor: border,
        borderWidth: 2,
        hoverOffset: 8
      }]
    }

    preparedOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: showLegend,
          position: 'bottom',
          labels: {
            color: foreground,
            padding: 20,
            font: {
              size: 12
            }
          }
        },
        title: {
          display: !!title,
          text: title,
          color: foreground,
          font: {
            size: 16,
            weight: 'bold'
          },
          padding: 20
        },
        tooltip: {
          enabled: showTooltip,
          backgroundColor: card,
          titleColor: foreground,
          bodyColor: foreground,
          borderColor: resolvedColor,
          borderWidth: 1,
          cornerRadius: 8
        }
      },
      onClick: (_event: any, elements: any) => {
        if (elements.length > 0 && onDataPointClick) {
          const index = elements[0].index
          onDataPointClick(data[index], index)
        }
      }
    }
  } else {
    // Line, Bar, Area charts
    const backgroundColor = gradient 
      ? {
          type: 'linear' as const,
          x: 0,
          y: 0,
          x2: 0,
          y2: height,
          colorStops: [
            { offset: 0, color: withAlpha(resolvedColor, 0.2) },
            { offset: 100, color: withAlpha(resolvedColor, 0.02) }
          ]
        }
      : type === 'area' ? withAlpha(resolvedColor, 0.2) : resolvedColor

    preparedData = {
      labels: data.map(point => point.x),
      datasets: [{
        label: title || 'Data',
        data: data.map(point => point.y),
        borderColor: resolvedColor,
        backgroundColor: type === 'area' ? backgroundColor : resolvedColor,
        fill: type === 'area',
        tension: curved ? 0.4 : 0,
        borderWidth: 2,
        pointRadius: type === 'bar' ? 0 : 4,
        pointHoverRadius: type === 'bar' ? 0 : 8,
        pointHoverBackgroundColor: resolvedColor,
        pointHoverBorderColor: background,
        pointHoverBorderWidth: 2,
        pointBackgroundColor: resolvedColor,
        pointBorderColor: border,
        pointBorderWidth: 2,
        // Make bar charts look better with single / few points
        barPercentage: type === 'bar' ? 0.4 : undefined,
        categoryPercentage: type === 'bar' ? 0.6 : undefined,
      }]
    }

    preparedOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: animationDuration,
        easing: 'easeInOutQuart'
      },
      plugins: {
        legend: {
          display: showLegend,
          labels: {
            color: foreground,
            font: {
              size: 12
            }
          }
        },
        title: {
          display: !!title,
          text: title,
          color: foreground,
          font: {
            size: 16,
            weight: 'bold'
          },
          padding: 20
        },
        tooltip: {
          enabled: showTooltip,
          backgroundColor: card,
          titleColor: foreground,
          bodyColor: foreground,
          borderColor: resolvedColor,
          borderWidth: 1,
          cornerRadius: 8,
          displayColors: false
        }
      },
      scales: {
        x: {
          grid: {
            display: showGrid,
            color: border,
            drawBorder: false
          },
          ticks: {
            color: mutedForeground,
            font: {
              size: 12
            }
          },
          // Center bars and points better when there are few data points
          offset: type === 'bar'
        },
        y: {
          grid: {
            display: showGrid,
            color: border,
            drawBorder: false
          },
          ticks: {
            color: mutedForeground,
            font: {
              size: 12
            },
            callback: (value: any) => {
              return typeof value === 'number' ? value.toLocaleString() : value
            }
          },
          beginAtZero: true
        }
      },
      interaction: {
        intersect: false,
        mode: 'index'
      },
      elements: {
        point: {
          hoverRadius: 8,
          hoverBorderWidth: 2
        }
      },
      onClick: (_event: any, elements: any) => {
        if (elements.length > 0 && onDataPointClick) {
          const index = elements[0].index
          onDataPointClick(data[index], index)
        }
      }
    }
  }

  if (!preparedData || !preparedOptions) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="animate-pulse text-muted-foreground">Loading chart...</div>
      </div>
    )
  }

  const renderChart = () => {
    const containerStyle: React.CSSProperties = {
      height,
      width: width || '100%',
      position: 'relative'
    }

    switch (type) {
      case 'line':
        return (
          <div style={containerStyle}>
            <Line data={preparedData} options={preparedOptions} />
          </div>
        )
      case 'bar':
        return (
          <div style={containerStyle}>
            <Bar data={preparedData} options={preparedOptions} />
          </div>
        )
      case 'area':
        return (
          <div style={containerStyle}>
            <Line data={preparedData} options={preparedOptions} />
          </div>
        )
      case 'doughnut':
        return (
          <div style={containerStyle}>
            <Doughnut data={preparedData} options={preparedOptions} />
          </div>
        )
      case 'pie':
        return (
          <div style={containerStyle}>
            <Pie data={preparedData} options={preparedOptions} />
          </div>
        )
      default:
        return (
          <div style={containerStyle}>
            <Line data={preparedData} options={preparedOptions} />
          </div>
        )
    }
  }

  return (
    <div className="w-full chart-container">
      {renderChart()}
    </div>
  )
}

export default SimpleNextJSChart
